import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import { Box, CircularProgress, Typography } from '@mui/material';
import type { FolderTreeNode } from '@/utils/buildFolderTree';

const FolderTreeItem = ({ node, disabledFolderIds }: { node: FolderTreeNode; disabledFolderIds: Set<string> }) => {
  const isDisabled = disabledFolderIds.has(node.id);

  return (
    <TreeItem
      itemId={node.id}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <FolderIcon sx={{ fontSize: 20, color: isDisabled ? 'text.disabled' : 'primary.main' }} />
          <Typography
            variant='body2'
            sx={{
              color: isDisabled ? 'text.disabled' : 'text.primary',
              fontStyle: isDisabled ? 'italic' : 'normal',
            }}
          >
            {node.name}
          </Typography>
        </Box>
      }
      disabled={isDisabled}
    >
      {node.children.map(child => (
        <FolderTreeItem key={child.id} node={child} disabledFolderIds={disabledFolderIds} />
      ))}
    </TreeItem>
  );
};

export const FolderTree = ({
  folderTree,
  selectedFolderId,
  expandedItems,
  disabledFolderIds,
  isLoading,
  onSelect,
  onExpandedItemsChange,
}: {
  folderTree: FolderTreeNode[];
  selectedFolderId: string | undefined;
  expandedItems: string[];
  disabledFolderIds: Set<string>;
  isLoading: boolean;
  onSelect: (event: React.SyntheticEvent | null, itemId: string | null) => void;
  onExpandedItemsChange: (event: React.SyntheticEvent | null, itemIds: string[]) => void;
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const selectedItem = selectedFolderId ?? 'root';

  const handleItemClick = (event: React.MouseEvent, itemId: string) => {
    const target = event.target as HTMLElement;
    const isExpandIcon = target.closest('.MuiTreeItem-iconContainer');
    if (isExpandIcon) {
      return;
    }
    onSelect(event, itemId);
  };

  return (
    <SimpleTreeView
      selectedItems={selectedItem}
      expandedItems={['root', ...expandedItems]}
      onItemClick={handleItemClick}
      onExpandedItemsChange={(event, itemIds) => onExpandedItemsChange(event, itemIds)}
      sx={{
        flexGrow: 1,
        overflowY: 'auto',
        '& .MuiTreeItem-content': {
          borderRadius: 1,
        },
        '& .Mui-selected': {
          backgroundColor: 'action.selected',
        },
      }}
    >
      <TreeItem
        itemId='root'
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <HomeIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant='body2' fontWeight={500}>
              Assets home folder
            </Typography>
          </Box>
        }
      >
        {folderTree.map(node => (
          <FolderTreeItem key={node.id} node={node} disabledFolderIds={disabledFolderIds} />
        ))}
      </TreeItem>
    </SimpleTreeView>
  );
};
